import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useWaLink } from "@/context/SiteDataContext";
import WhatsAppChooser, { useWhatsAppPresets } from "@/components/common/WhatsAppChooser";

/**
 * The persistent WhatsApp bubble.
 *
 * This is the most-tapped WhatsApp entry point on the site, so it goes through
 * the same question chooser as the inline buttons — otherwise the preset
 * questions would rarely be seen.
 */
export default function FloatingWhatsApp() {
  const waLink = useWaLink();
  const presets = useWhatsAppPresets();
  const [open, setOpen] = useState(false);

  const bubbleClass =
    "fixed bottom-6 right-6 z-40 w-14 h-14 flex items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_8px_28px_rgba(37,211,102,0.45)]";
  const animation = {
    initial: { scale: 0, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    transition: { delay: 1, type: "spring", stiffness: 260, damping: 20 },
    whileHover: { scale: 1.08 },
  };

  const inner = (
    <>
      <MessageCircle className="w-7 h-7" />
      <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20" />
    </>
  );

  if (presets.length === 0) {
    return (
      <motion.a
        href={waLink()}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp"
        aria-label="WhatsApp Athena Scientific"
        {...animation}
        className={bubbleClass}
      >
        {inner}
      </motion.a>
    );
  }

  return (
    <>
      <motion.button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="floating-whatsapp"
        aria-label="WhatsApp Athena Scientific"
        {...animation}
        className={bubbleClass}
      >
        {inner}
      </motion.button>
      <WhatsAppChooser open={open} onClose={() => setOpen(false)} />
    </>
  );
}
